<template>
  <div class="set-parameters">
    <h4 class="card-title">{{ $t('routeOptimization.label.assignmentParameter') }}</h4>

    <b-card>
      <div class="d-flex flex-column gap-2">
        <h4 class="card-title">{{ $t('routeOptimization.label.deliveryTime') }}</h4>

        <div class="row">
          <div class="col-12 col-md-3">
            <FormulateInput
              class="p-0"
              name="expected_departure_on"
              type="date-picker"
              :label="$t('routeOptimization.fields.departureTime')"
              :placeholder="$t('routeOptimization.placeholder.selectDepartureTime')"
              validation="bail|required|timeGteNow"
              datepickerType="datetime"
              valueType="YYYY-MM-DDTHH:mm:ss.SSSZ"
              format="DD MMM YYYY HH:mm"
              :confirmText="$t('global.action.apply')"
              :tooltip="$t('routeOptimization.tooltip.operationalHoursInfo')"
              :disabled="isLoading"
              :showSecond="false"
              :confirm="true"
              :help="true"
              :error-behavior="'live'"
              :validationMessages="departureTimeValidationMessages"
              :lang="{
                formatLocale: {
                  weekdaysMin: ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'],
                },
              }"
            />
          </div>

          <div class="col-12 col-md-3">
            <FormulateInput
              class="p-0"
              name="expected_end_on"
              type="date-picker"
              :label="$t('routeOptimization.fields.assignmentEndTime')"
              :placeholder="$t('routeOptimization.placeholder.selectAssignmentEndTime')"
              validation="bail|timeGteNow|timeGteField:expected_departure_on|endTimeNotEqualDeparture"
              datepickerType="datetime"
              valueType="YYYY-MM-DDTHH:mm:ss.SSSZ"
              format="DD MMM YYYY HH:mm"
              :confirmText="$t('global.action.apply')"
              :disabled="isLoading"
              :showSecond="false"
              :confirm="true"
              :error-behavior="'live'"
              :validationMessages="endTimeValidationMessages"
              :validation-rules="{ endTimeNotEqualDeparture }"
              :lang="{
                formatLocale: {
                  weekdaysMin: ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'],
                },
              }"
            />
          </div>
        </div>

        <ActivityDuration
          ref="activity-duration-ref"
          :activityDuration="formValues['options.is_service_time_per_activity']"
          :serviceTime="formValues['options.service_time']"
          @update:activityDuration="
            $emit('update:formValues', {
              key: 'options.is_service_time_per_activity',
              value: $event,
            })
          "
          @update:serviceTime="
            $emit('update:formValues', { key: 'options.service_time', value: $event })
          "
        />

        <BreakTime :disabled="isBreakTimeDisabled" />

        <TrafficLevel
          :trafficLevel="formValues['options.traffic_level']"
          @update:trafficLevel="
            $emit('update:formValues', {
              key: 'options.traffic_level',
              value: $event,
            })
          "
        />

        <hr />

        <h4 class="card-title">{{ $t('routeOptimization.label.routeOption') }}</h4>

        <div class="d-flex gap-3">
          <ChecklistInput
            v-if="!isRoSwatActive"
            id="minimum_vehicle"
            :label="$t('routeOptimization.fields.minimizeVehicles')"
            :tooltip="$t('routeOptimization.tooltip.minimizeVehicles')"
            :modelValue="formValues['options.minimum_vehicle']"
            @update:modelValue="
              $emit('update:formValues', { key: 'options.minimum_vehicle', value: $event })
            "
          />
          <ChecklistInput
            id="avoid_tolls"
            :label="$t('routeOptimization.fields.avoidToll')"
            :tooltip="$t('routeOptimization.tooltip.avoidToll')"
            :modelValue="formValues['options.avoid_tolls']"
            @update:modelValue="
              $emit('update:formValues', { key: 'options.avoid_tolls', value: $event })
            "
          />
          <ChecklistInput
            v-if="!isRoSwatActive"
            id="round_trip"
            :label="$t('routeOptimization.fields.roundTrip')"
            :tooltip="$t('routeOptimization.tooltip.roundTrip')"
            :modelValue="formValues['options.round_trip']"
            @update:modelValue="
              $emit('update:formValues', { key: 'options.round_trip', value: $event })
            "
          />
          <ChecklistInput
            v-if="false"
            id="pickup_location_optimization"
            :label="$t('routeOptimization.fields.doPickupOptimization')"
            :tooltip="$t('routeOptimization.tooltip.doPickupOptimization')"
            :modelValue="formValues['options.pickup_location_optimization']"
            @update:modelValue="
              $emit('update:formValues', { key: 'options.pickup_location_optimization', value: $event })
            "
          />
          <ChecklistInput
            v-if="hasAccessAll(['route-optimization-manually-assign-vehicle:read'])"
            id="manually_assign_vehicle"
            :label="$t('routeOptimization.fields.manuallyAssignVehicle')"
            :tooltip="$t('routeOptimization.tooltip.manuallyAssignVehicle')"
            :modelValue="formValues['options.manually_assign_vehicle']"
            @update:modelValue="
              $emit('update:formValues', { key: 'options.manually_assign_vehicle', value: $event })
            "
          />
        </div>

        <hr v-if="!isRoSwatActive" />

        <h4 v-if="!isRoSwatActive" class="card-title">{{ $t('routeOptimization.label.considerCapacity') }}</h4>

        <div v-if="!isRoSwatActive" class="d-flex gap-3">
          <ChecklistInput
            id="weight_capacity"
            :label="$t('routeOptimization.fields.basedOnWeight')"
            :tooltip="$t('routeOptimization.tooltip.basedOnWeight')"
            :modelValue="formValues['options.weight_capacity']"
            @update:modelValue="
              $emit('update:formValues', { key: 'options.weight_capacity', value: $event })
            "
          />
          <ChecklistInput
            id="volume_capacity"
            :label="$t('routeOptimization.fields.basedOnVolume')"
            :tooltip="$t('routeOptimization.tooltip.basedOnVolume')"
            :modelValue="formValues['options.volume_capacity']"
            @update:modelValue="
              $emit('update:formValues', { key: 'options.volume_capacity', value: $event })
            "
          />
          <ChecklistInput
            v-if="false"
            id="box_type_capacity"
            :label="$t('routeOptimization.fields.basedOnBoxType')"
            :tooltip="$t('routeOptimization.tooltip.basedOnBoxType')"
            :modelValue="formValues['options.box_type_capacity']"
            @update:modelValue="
              $emit('update:formValues', { key: 'options.box_type_capacity', value: $event })
            "
          />
        </div>
      </div>
    </b-card>

    <h4 class="card-title">{{ $t('routeOptimization.label.selectVehicle') }}</h4>

    <TableSelectVehicle
      v-show="isVisible"
      ref="table-select-vehicle-ref"
      :vehicleReoptimizeIds="vehicleReoptimizeIds"
      :selectedVehicleIds="formValues?.vehicle_ids || []"
      :userGroupId="formValues?.user_group_id"
      :userGroupVgroups="fullObjects?.user_group?.vgroups"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import TableSelectVehicle from './TableSelectVehicle'
import ActivityDuration from './components/ActivityDuration'
import ActivityDurationVRPV1 from './components/ActivityDurationVRPV1'
import ActivityDurationPDP from './components/ActivityDurationPDP'
import BreakTime from './components/BreakTime'
import TrafficLevel from './components/TrafficLevel'
import ChecklistInput from './components/ChecklistInput'

export default {
  components: {
    TableSelectVehicle,
    ActivityDuration,
    ActivityDurationVRPV1,
    ActivityDurationPDP,
    BreakTime,
    TrafficLevel,
    ChecklistInput,
  },
  props: {
    formValues: {
      type: Object,
      default: () => {},
    },
    fullObjects: {
      type: Object,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isVisible: {
      type: Boolean,
      default: true,
    },
    isReoptimize: {
      type: Boolean,
      default: false,
    },
    vehicleReoptimizeIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:formValues'],
  mixins: [Helper],
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
      getFeatureStrategies: 'identity/getFeatureStrategies',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    isBreakTimeDisabled() {
      const departureTime = this.formValues.expected_departure_on
      const endTime = this.formValues.expected_end_on

      if (!endTime) {
        return true
      }

      if (!departureTime) {
        return true
      }

      try {
        const departureDate = new Date(departureTime)
        const endDate = new Date(endTime)

        const departureDateEndOfDay = new Date(departureDate)
        departureDateEndOfDay.setHours(23, 59, 59, 999)

        return endDate > departureDateEndOfDay
      } catch (error) {
        console.error('Error parsing dates:', error)
        return true
      }
    },
    departureTimeValidationMessages() {
      return {
        timeGteNow: this.$t('routeOptimization.validation.departureNotPastToday'),
      }
    },
    endTimeValidationMessages() {
      return {
        timeGteNow: this.$t('routeOptimization.validation.endTimeNotPastToday'),
        timeGteField: this.$t('routeOptimization.validation.endTimeBeforeDeparture'),
        endTimeNotEqualDeparture: this.$t(
          'routeOptimization.validation.endTimeShouldNotEqualDeparture'
        ),
      }
    },
    isRoSwatActive() {
      const isSamator = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false
      return isSamator && isActive
    },
  },
  watch: {
    isBreakTimeDisabled: {
      handler(newValue) {
        if (newValue) {
          this.resetBreakTimeValues()
        }
      },
      immediate: true,
    },
  },
  methods: {
    endTimeNotEqualDeparture(context) {
      const endTime = context?.value
      const departureTime = this.formValues?.expected_departure_on

      if (!endTime || !departureTime) return true
      if (endTime === 'Invalid date' || departureTime === 'Invalid date') return true

      const endDate = new Date(endTime)
      const departureDate = new Date(departureTime)

      if (Number.isNaN(endDate.getTime()) || Number.isNaN(departureDate.getTime())) return true

      return endDate.getTime() !== departureDate.getTime()
    },
    resetBreakTimeValues() {
      this.$emit('update:formValues', {
        key: 'options.fleet_shift_time.start_break_time',
        value: null,
      })

      this.$emit('update:formValues', {
        key: 'options.fleet_shift_time.end_break_time',
        value: null,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.set-parameters {
  &__checkbox-label {
    color: $mep-color-text-300;
    padding: 0;
  }
}
</style>
